if (!customElements.get('shades-carousel')) {
  const MOBILE_BREAKPOINT = 750;
  const TABLET_BREAKPOINT = 1025;
  const SWIPE_THRESHOLD = 50;
  const RESIZE_DEBOUNCE_MS = 100;
 
  customElements.define(
    'shades-carousel',
    class ShadesCarousel extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.totalShades = parseInt(this.dataset.totalShades, 10) || 5;
        this.desktopVisible = parseInt(this.dataset.desktopVisible, 10) || 5;
        this.tabletVisible = parseInt(this.dataset.tabletVisible, 10) || 3;
        this.mobileVisible = parseInt(this.dataset.mobileVisible, 10) || 2;
 
        this.startX = 0; 
        this.endX = 0;
        this.endY = 0;
        this.isDragging = false;
        this.isScrolling = false;
        this.dragStartScroll = 0;
        this.resizeTimeout = null;
        this.scrollTimeout = null;
        this.isProgrammaticScroll = false;
        this.abortController = null;

        this.boundHandlePointerStart = this.handlePointerStart.bind(this);
        this.boundHandlePointerMove = this.handlePointerMove.bind(this);
        this.boundHandlePointerEnd = this.handlePointerEnd.bind(this);
        this.boundHandleResize = this.handleResize.bind(this);
        this.boundHandleScroll = this.handleScroll.bind(this);
      }
 
      connectedCallback() {
        this.track = this.querySelector('.shades-carousel__track');
        this.viewport = this.querySelector('.shades-carousel__viewport');
        this.prevButton = this.querySelector('.shades-carousel__button--prev');
        this.nextButton = this.querySelector('.shades-carousel__button--next');
 
        if (!this.track) return;
 
        this.abortController = new AbortController();
        const { signal } = this.abortController;
 
        this.prevButton?.addEventListener('click', () => this.navigate(-1), { signal });
        this.nextButton?.addEventListener('click', () => this.navigate(1), { signal });

        this.track.addEventListener('mousedown', this.boundHandlePointerStart, { signal });
        document.addEventListener('mousemove', this.boundHandlePointerMove, { signal });
        document.addEventListener('mouseup', this.boundHandlePointerEnd, { signal });

        if (this.viewport) {
          this.viewport.addEventListener('scroll', this.boundHandleScroll, { passive: true, signal });
        }

        window.addEventListener('resize', this.boundHandleResize, { signal });
 
        this.updateButtons();
        this.updatePosition();
      }
 
      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
 
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
          this.resizeTimeout = null;
        }

        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
          this.scrollTimeout = null;
        }
      }
 
   
      getVisibleShades() {
        const width = window.innerWidth;
        if (width < MOBILE_BREAKPOINT) return this.mobileVisible;
        if (width < TABLET_BREAKPOINT) return this.tabletVisible;
        return this.desktopVisible;
      }

      getMaxIndex() {
        const visibleShades = this.getVisibleShades();
        return Math.max(0, this.totalShades - visibleShades);
      }
 

      getCurrentTranslatePercent() {
        const shadeWidthPercent = 100 / this.totalShades;
        return -(this.currentIndex * shadeWidthPercent);
      }
 
      getTranslateBoundsPercent() {
        const shadeWidthPercent = 100 / this.totalShades;
        const min = -(this.getMaxIndex() * shadeWidthPercent);
        const max = 0;
        return { min, max };
      }
      navigate(direction) {
        const maxIndex = this.getMaxIndex();
        const newIndex = this.currentIndex + direction;
 
        if (newIndex >= 0 && newIndex <= maxIndex) {
          if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = null;
          }
          
          this.currentIndex = newIndex;
          this.updatePosition();
          this.updateButtons();
        }
      }

      goToIndex(index) {
        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
          this.scrollTimeout = null;
        }
        
        const maxIndex = this.getMaxIndex();
        this.currentIndex = Math.max(0, Math.min(index, maxIndex));
        this.updatePosition();
        this.updateButtons();
      }
 
      updatePosition() {
        if (!this.viewport) return;
        
        this.isProgrammaticScroll = true;
        const viewportWidth = this.viewport.offsetWidth;
        const shadeWidth = viewportWidth / this.getVisibleShades();
        this.viewport.scrollLeft = this.currentIndex * shadeWidth;
        
        setTimeout(() => {
          this.isProgrammaticScroll = false;
        }, 400);
      }
 
      updateButtons() {
        const maxIndex = this.getMaxIndex();
 
        if (this.prevButton) {
          this.prevButton.disabled = this.currentIndex <= 0;
        }
        if (this.nextButton) {
          this.nextButton.disabled = this.currentIndex >= maxIndex;
        }
      }
 
      handleResize() {
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }
 
        this.resizeTimeout = setTimeout(() => {
          const maxIndex = this.getMaxIndex();
          if (this.currentIndex > maxIndex) {
            this.currentIndex = maxIndex;
            this.updatePosition();
          }
          this.updateButtons();
        }, RESIZE_DEBOUNCE_MS);
      }

      handleScroll() {
        if (this.isDragging || this.isProgrammaticScroll) return; // Don't interfere with mouse dragging or programmatic scrolling

        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
        }

        this.scrollTimeout = setTimeout(() => {
          if (!this.viewport) return;

          const viewportWidth = this.viewport.offsetWidth;
          const shadeWidth = viewportWidth / this.getVisibleShades();
          const scrollLeft = this.viewport.scrollLeft;
          
          const targetIndex = Math.round(scrollLeft / shadeWidth);
          const maxIndex = this.getMaxIndex();
          this.currentIndex = Math.max(0, Math.min(targetIndex, maxIndex));
          
          this.isProgrammaticScroll = true;
          this.viewport.scrollLeft = this.currentIndex * shadeWidth;
          this.updateButtons();
          
          setTimeout(() => {
            this.isProgrammaticScroll = false;
          }, 100);
        }, 100);
      }

      getPointerX(event) {
        return event.touches ? event.touches[0].clientX : event.clientX;
      }

      getPointerY(event) {
        return event.touches ? event.touches[0].clientY : event.clientY;
      }
 

      handlePointerStart(event) {
        if (event.type === 'mousedown') {
          event.preventDefault();
          this.isDragging = true; 
        }
 
        const x = this.getPointerX(event);
        const y = this.getPointerY(event);
        this.startX = x;
        this.startY = y;
        this.endX = x;
        this.endY = y;
        this.isScrolling = false;
       
        if (event.type !== 'touchstart') {
          this.isDragging = true;
        }
       
        if (this.viewport) {
          this.dragStartScroll = this.viewport.scrollLeft;
          this.viewport.style.scrollBehavior = 'auto';
        }

        if (event.type === 'mousedown' && this.track) {
          this.track.style.cursor = 'grabbing';
        }
      }

      handlePointerMove(event) {
        if (!this.viewport) return;
       
        const currentX = this.getPointerX(event);
        const currentY = this.getPointerY(event);
        if (event.type === 'touchmove' && !this.isDragging && !this.isScrolling) {
          const deltaX = Math.abs(currentX - this.startX);
          const deltaY = Math.abs(currentY - this.startY);

          if (deltaX > 10 || deltaY > 10) {
            if (deltaY > deltaX) {
              this.isScrolling = true;
              return;
            } else {
              this.isDragging = true;
            }
          } else {
            return;
          }
        }
       
        if (!this.isDragging) return;
       
        if (event.type === 'touchmove') {
          event.preventDefault();
        }
       
        this.endX = currentX;
        this.endY = currentY;
 
        const deltaX = this.endX - this.startX;
        let newScrollLeft = this.dragStartScroll - deltaX;

        const maxScroll = this.viewport.scrollWidth - this.viewport.offsetWidth;
        if (newScrollLeft < 0) newScrollLeft = 0;
        if (newScrollLeft > maxScroll) newScrollLeft = maxScroll;

        this.viewport.scrollLeft = newScrollLeft;
      }
 
      handlePointerEnd() {
        if (!this.isDragging) {
          this.isScrolling = false;
          this.startX = 0;
          this.startY = 0;
          this.endX = 0;
          this.endY = 0;
          return;
        }
 
        this.isDragging = false;
        this.isScrolling = false;
 
        if (this.track) {
          this.track.style.cursor = '';
        }

        if (!this.viewport) return;

        this.viewport.style.scrollBehavior = '';

        const deltaX = this.endX - this.startX;

        if (Math.abs(deltaX) < SWIPE_THRESHOLD) {
          this.updatePosition();
        } else {
          const viewportWidth = this.viewport.offsetWidth;
          const shadeWidth = viewportWidth / this.getVisibleShades();
          const scrollLeft = this.viewport.scrollLeft;
          const targetIndex = Math.round(scrollLeft / shadeWidth);
          this.currentIndex = Math.max(0, Math.min(targetIndex, this.getMaxIndex()));
          this.updatePosition();
          this.updateButtons();
        }

        this.startX = 0;
        this.startY = 0;
        this.endX = 0;
        this.endY = 0;
      }
    }
  );
}
 